import { UserRoundPlusIcon, HomeIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useTitle } from 'react-use';

import Loading from '@/components/atoms/Loading/Loading';
import { MODAL_MEMBER_INVITE } from '@/components/organisms/Modals/ModalConductor';
import {
  PageSubHeader,
  PageSubHeaderActions,
  PageSubHeaderBreadcrumbs,
  PageSubHeaderDeskDropdown,
  DeskDropdown,
} from '@/components/organisms/PageSubHeader';
import Layout from '@/components/templates/Layout';
import PageContentWrapper from '@/components/templates/PageContentWrapper';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbList,
  BreadcrumbPage,
} from '@/components/ui/breadcrumb';
import { Button } from '@/components/ui/button';
import { useDeskId } from '@/hooks/useDeskId';
import useDesks from '@/hooks/useDesks';
import useHomeCheckList, { AccountUserPrefsEnum } from '@/hooks/useHomeCheckList';
import { cn } from '@/lib/utils';
import { pushModal } from '@/redux/modals/actions';
import { getPermissions, selectPermissions } from '@/redux/permissions/selectors';
import { selectIframeView } from '@/redux/user/selectors';
import useSegments from '@/segment/useSegments';
import { actions } from '@/shared/types/permissions';
import { PageName } from '@/shared/types/segment';
import { RootState } from '@/shared/types/state';

import HomeFieldset from './fieldsets/HomeFieldset/HomeFieldset';
import HomeOverview from './fieldsets/HomeOverview/HomeOverview';
import SidebarFieldset from './fieldsets/SidebarFieldset/SidebarFieldset';
import HomeCompleted from './HomeCompleted';
import useHomeInit from './useHomeInit';

import styles from './Home.module.scss';

const Home = () => {
  useHomeInit();
  useSegments(PageName.HOME);
  const dispatch = useDispatch();
  const permissions = useSelector(selectPermissions);
  const { t } = useTranslation();
  const isIframeView = useSelector(selectIframeView);
  const handleClick = () => {
    dispatch(pushModal(MODAL_MEMBER_INVITE));
  };
  const { desks } = useDesks();
  const deskId = useDeskId();
  const desk = desks?.find((desk) => desk.desk_id === deskId);
  useTitle(t('pages.home', { 0: desk?.name ?? '-' }));

  const { accountUserChecklist } = useHomeCheckList();

  const isCompleted = accountUserChecklist[AccountUserPrefsEnum.COMPLETED];

  const canAssign = useSelector((state: RootState) =>
    getPermissions(state, 'roles', actions.ASSIGN)
  );

  const canReadBraiins = useSelector((state: RootState) =>
    getPermissions(state, 'brains', actions.READ)
  );

  if (!permissions) {
    return <Loading />;
  }
  const inviteAction = canAssign ? (
    <Button onClick={handleClick} variant="ghost">
      <UserRoundPlusIcon size={16} className="text-accent" />
      {t('members.invite')}
    </Button>
  ) : undefined;

  return (
    <Layout>
      <PageSubHeader
        breadcrumbs={
          <PageSubHeaderBreadcrumbs>
            <Breadcrumb>
              <BreadcrumbList>
                <BreadcrumbItem>
                  <BreadcrumbPage>
                    <div className="flex h-8 w-8 items-center justify-center">
                      <HomeIcon size={16} className="text-foreground-muted" />
                    </div>
                    <div className="mr-4 ml-2 h-4 w-px bg-border" />
                    <span className="text-sm leading-5 font-normal">{t('home.home')}</span>
                  </BreadcrumbPage>
                </BreadcrumbItem>
              </BreadcrumbList>
            </Breadcrumb>
          </PageSubHeaderBreadcrumbs>
        }
        deskDropdown={
          <PageSubHeaderDeskDropdown>
            <DeskDropdown />
          </PageSubHeaderDeskDropdown>
        }
        actions={
          !isIframeView && inviteAction ? (
            <PageSubHeaderActions>{inviteAction}</PageSubHeaderActions>
          ) : undefined
        }
      />
      <PageContentWrapper>
        {canReadBraiins && (
          <div className={cn(styles.overviewContainer, '!mb-4')}>
            <HomeOverview />
          </div>
        )}
        {!isCompleted && !isIframeView && (
          <div className={styles.container}>
            <HomeFieldset />
            <SidebarFieldset />
          </div>
        )}
        {(isCompleted || isIframeView) && <HomeCompleted />}
      </PageContentWrapper>
    </Layout>
  );
};

export default Home;
